//@ts-nocheck
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Custom hook to manage booking step navigation logic
 * Centralizes step visibility and navigation decisions
 *
 * @returns {Object} Step navigation utilities
 */
export const useBookingSteps = () => {
  const steps = useSelector((state) => state.booking.steps);

  const activeStep = useMemo(() => steps?.find((step) => step.active), [steps]);

  const addonsStep = useMemo(
    () => steps?.find((s) => s.key === 'addons'),
    [steps]
  );

  const shouldShowAddOnsStep = useMemo(
    () => addonsStep?.shouldRender ?? false,
    [addonsStep]
  );

  /**
   * Get the step number for a given step key, accounting for conditional steps
   * @param {string} stepKey - The step key (e.g., 'payment')
   * @returns {number} The 1-indexed step number
   */
  const getStepNumber = (stepKey) => {
    const visibleSteps = steps.filter((s) => s.shouldRender);
    const stepIndex = visibleSteps.findIndex((s) => s.key === stepKey);
    return stepIndex >= 0 ? stepIndex + 1 : 0;
  };

  /**
   * Determine the next step key after the current active step
   * @returns {string|null} The next step key or null if at the end
   */
  const getNextStepKey = () => {
    if (!activeStep) return null;

    switch (activeStep.key) {
      case 'plan':
        return 'passenger';
      case 'passenger':
        return shouldShowAddOnsStep ? 'addons' : 'payment';
      case 'addons':
        return 'payment';
      case 'payment':
        return 'success';
      default:
        return null;
    }
  };

  /**
   * Determine the previous step key before the current active step
   * @returns {string|null} The previous step key or null if at the start
   */
  const getPreviousStepKey = () => {
    if (!activeStep) return null;

    switch (activeStep.key) {
      case 'passenger':
        return 'plan';
      case 'addons':
        return 'passenger';
      case 'payment':
        return shouldShowAddOnsStep ? 'addons' : 'passenger';
      case 'success':
        return 'payment';
      default:
        return null;
    }
  };

  /**
   * Get the appropriate CTA button label for the current step
   * @param {Function} t - Translation function
   * @param {boolean} isFullCapacity - Whether trip is at full capacity
   * @param {string} formattedAmount - Formatted payment amount
   * @returns {string} The button label
   */
  const getCtaLabel = (t, isFullCapacity = false, formattedAmount = '') => {
    if (!activeStep) return '';

    switch (activeStep.key) {
      case 'plan':
        return isFullCapacity
          ? t('trip:contactOperator', 'Contact Operator')
          : t('trip:goToPassengers', 'Go To Passengers');
      case 'passenger':
        return shouldShowAddOnsStep
          ? t('trip:goToAddOns', 'Go To Add-Ons')
          : t('trip:goToPayments', 'Go To Payments');
      case 'addons':
        return t('trip:goToPayments', 'Go To Payments');
      case 'payment':
        return t('trip:completeBookingAndPay', {
          value: formattedAmount
        });
      default:
        return '';
    }
  };

  return {
    steps,
    activeStep,
    addonsStep,
    shouldShowAddOnsStep,
    getStepNumber,
    getNextStepKey,
    getPreviousStepKey,
    getCtaLabel
  };
};
